import { ComponentProps, forwardRef } from "react";
import { BMCalendar, type BMCalendarRef } from "@traveloka/district-bloom";

import CalendarPickerComponent from "./CalendarPickerComponent";
import CalendarPickerRangeRhf from "./CalendarPickerRangeRhf";
import CalendarPickerSingleRhf from "./CalendarPickerSingleRhf";

type BMCalendarProps = ComponentProps<typeof BMCalendar>;
type CalendarBaseProps = Omit<BMCalendarProps, "date" | "onChange" | "testID">;

export type CalendarPickerSingleRhfProps = CalendarBaseProps & {
  name: string;
  onChange?(): void;
  testID?: string;
};

export type CalendarPickerRangeRhfProps = CalendarBaseProps & {
  name: [string, string];
  onChange?(): void;
  testID?: string;
};

export type CalendarPickerComponentProps = CalendarBaseProps & {
  name?: undefined;
  testID?: string;
} & (SingleCalendarProps | RangeCalendarProps);

export type SingleCalendarProps = {
  value?: Date;
  onChange(value: Date, externalState?: Record<string, unknown>): void;
};

export type RangeCalendarProps = {
  value: [Date | undefined, Date | undefined];
  onChange(
    value: [Date, Date | undefined],
    externalState?: Record<string, unknown>
  ): void;
};

export type CalendarPickerProps =
  | CalendarPickerSingleRhfProps
  | CalendarPickerRangeRhfProps
  | CalendarPickerComponentProps;

const CalendarPicker = forwardRef<BMCalendarRef, CalendarPickerProps>(
  function CalendarPicker(props, ref) {
    if (isSingleRhf(props)) {
      return <CalendarPickerSingleRhf {...props} />;
    }

    if (isRangeRhf(props)) {
      return <CalendarPickerRangeRhf {...props} />;
    }

    if (isComponent(props)) {
      return <CalendarPickerComponent ref={ref} {...props} />;
    }

    return null;
  }
);

function isSingleRhf(
  p: CalendarPickerProps
): p is CalendarPickerSingleRhfProps {
  return typeof p.name === "string";
}

function isRangeRhf(p: CalendarPickerProps): p is CalendarPickerRangeRhfProps {
  return Array.isArray(p.name);
}

function isComponent(
  p: CalendarPickerProps
): p is CalendarPickerComponentProps {
  return p.name === undefined;
}

export default CalendarPicker;
